import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Copy, Bookmark, Grid3x3, Type as TypeIcon } from 'lucide-react';

const FONT_FAMILIES = ['Inter', 'Poppins', 'Montserrat', 'Playfair Display', 'Courier New'];
const ENTRANCE_EXIT = ['none', 'fade', 'slide_up', 'slide_left', 'pop'];

const Row = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-xs text-[#8A93A6]">{label}</label>
    {children}
  </div>
);
const SingleSlider = ({ value, onChange, min = 0, max = 100, step = 1, testId }) => (
  <div className="flex items-center gap-3">
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} className="flex-1" data-testid={testId} />
    <span className="text-xs text-white w-10 text-right">{Math.round(value)}</span>
  </div>
);

/**
 * Plain text/title overlay controls (caption-block items use
 * EditorCaptionControls instead). Covers typography, outline/shadow/
 * background, Transform, entrance/exit animation (genuinely applied in
 * EditorCompositionPreview.jsx), reusable style presets stored on the
 * composition itself, and the shared safe-zone guide toggle.
 */
export function EditorTextControls({ item, presets, onTextChange, onTransformChange, onDuplicate, onSaveAsPreset, onApplyPreset, showSafeZones, onToggleSafeZones }) {
  const [presetName, setPresetName] = useState('');
  const text = item.text || {};
  const transform = item.transform || { scale_percent: 100, position_x: 0, position_y: 0, rotation_degrees: 0, opacity_percent: 100 };
  const animation = text.animation || { entrance: 'none', exit: 'none', entrance_duration_seconds: 0.4, exit_duration_seconds: 0.4 };

  return (
    <div className="space-y-4 pt-2 border-t border-white/5" data-testid="text-controls-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#555E6E] uppercase tracking-wide flex items-center gap-1.5"><TypeIcon className="h-3 w-3" />Text & Title</p>
        <Button size="sm" variant="outline" className="h-6 text-[10px] border-white/10" onClick={onDuplicate} data-testid="text-duplicate-button">
          <Copy className="h-3 w-3 mr-1" />Duplicate
        </Button>
      </div>

      <Textarea
        value={text.text || ''} onChange={(e) => onTextChange({ text: e.target.value })} rows={2}
        className="bg-[#1C1F26] border-white/10 text-white text-sm" placeholder="Title text…" data-testid="text-content-input"
      />

      <div className="grid grid-cols-2 gap-3">
        <Row label="Font">
          <Select value={text.font_family || 'Inter'} onValueChange={(v) => onTextChange({ font_family: v })}>
            <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="text-font-select"><SelectValue /></SelectTrigger>
            <SelectContent>{FONT_FAMILIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
        </Row>
        <Row label="Size">
          <Input type="number" value={text.font_size || 32} onChange={(e) => onTextChange({ font_size: Number(e.target.value) })} className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="text-font-size-input" />
        </Row>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Row label="Color"><input type="color" value={text.color || '#FFFFFF'} onChange={(e) => onTextChange({ color: e.target.value })} className="h-8 w-full rounded bg-[#1C1F26] border border-white/10" data-testid="text-color-input" /></Row>
        <Row label="Weight">
          <Select value={text.font_weight || 'normal'} onValueChange={(v) => onTextChange({ font_weight: v })}>
            <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="text-weight-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="bold">Bold</SelectItem></SelectContent>
          </Select>
        </Row>
        <Row label="Align">
          <Select value={text.alignment || 'center'} onValueChange={(v) => onTextChange({ alignment: v })}>
            <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="text-align-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="center">Center</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent>
          </Select>
        </Row>
      </div>

      <div className="space-y-2 pt-2 border-t border-white/5">
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#8A93A6]">Shadow</span>
          <Switch checked={!!text.shadow_enabled} onCheckedChange={(v) => onTextChange({ shadow_enabled: v })} data-testid="text-shadow-switch" />
        </div>
        {text.shadow_enabled && (
          <div className="grid grid-cols-2 gap-3">
            <Row label="Shadow Color"><input type="color" value={text.shadow_color || '#000000'} onChange={(e) => onTextChange({ shadow_color: e.target.value })} className="h-8 w-full rounded bg-[#1C1F26] border border-white/10" data-testid="text-shadow-color-input" /></Row>
            <Row label="Blur"><SingleSlider value={text.shadow_blur ?? 4} min={0} max={40} onChange={(v) => onTextChange({ shadow_blur: v })} testId="text-shadow-blur-slider" /></Row>
          </div>
        )}
        <Row label="Outline Width"><SingleSlider value={text.outline_width ?? 0} min={0} max={20} onChange={(v) => onTextChange({ outline_width: v })} testId="text-outline-width-slider" /></Row>
        {(text.outline_width ?? 0) > 0 && (
          <Row label="Outline Color"><input type="color" value={text.outline_color || '#000000'} onChange={(e) => onTextChange({ outline_color: e.target.value })} className="h-8 w-16 rounded bg-[#1C1F26] border border-white/10" data-testid="text-outline-color-input" /></Row>
        )}
        <Row label="Background Opacity (%)">
          <SingleSlider value={text.background_opacity ?? 0} min={0} max={100} onChange={(v) => onTextChange({ background_opacity: v, background_color: text.background_color || '#000000' })} testId="text-bg-opacity-slider" />
        </Row>
      </div>

      <div className="space-y-3 pt-2 border-t border-white/5">
        <p className="text-xs text-[#555E6E] uppercase tracking-wide">Transform</p>
        <Row label="Scale (%)"><SingleSlider value={transform.scale_percent ?? 100} min={10} max={300} onChange={(v) => onTransformChange({ scale_percent: v })} testId="text-scale-slider" /></Row>
        <Row label="Position X"><SingleSlider value={transform.position_x ?? 0} min={-960} max={960} onChange={(v) => onTransformChange({ position_x: v })} testId="text-position-x-slider" /></Row>
        <Row label="Position Y"><SingleSlider value={transform.position_y ?? 0} min={-540} max={540} onChange={(v) => onTransformChange({ position_y: v })} testId="text-position-y-slider" /></Row>
        <Row label="Rotation (°)"><SingleSlider value={transform.rotation_degrees ?? 0} min={-180} max={180} onChange={(v) => onTransformChange({ rotation_degrees: v })} testId="text-rotation-slider" /></Row>
        <Row label="Opacity (%)"><SingleSlider value={transform.opacity_percent ?? 100} min={0} max={100} onChange={(v) => onTransformChange({ opacity_percent: v })} testId="text-opacity-slider" /></Row>
      </div>

      <div className="space-y-2 pt-2 border-t border-white/5">
        <p className="text-xs text-[#555E6E] uppercase tracking-wide">Entrance / Exit Animation</p>
        <div className="grid grid-cols-2 gap-3">
          <Row label="Entrance">
            <Select value={animation.entrance} onValueChange={(v) => onTextChange({ animation: { ...animation, entrance: v } })}>
              <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="text-entrance-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ENTRANCE_EXIT.map((a) => <SelectItem key={a} value={a}>{a.replace('_', ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </Row>
          <Row label="Exit">
            <Select value={animation.exit} onValueChange={(v) => onTextChange({ animation: { ...animation, exit: v } })}>
              <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="text-exit-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ENTRANCE_EXIT.map((a) => <SelectItem key={a} value={a}>{a.replace('_', ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </Row>
        </div>
      </div>

      <div className="space-y-2 pt-2 border-t border-white/5">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs text-[#8A93A6]"><Grid3x3 className="h-3.5 w-3.5" />Safe-Zone Guides</span>
          <Switch checked={!!showSafeZones} onCheckedChange={onToggleSafeZones} data-testid="text-safe-zones-switch" />
        </div>
      </div>

      <div className="space-y-2 pt-2 border-t border-white/5">
        <p className="text-xs text-[#555E6E] uppercase tracking-wide flex items-center gap-1.5"><Bookmark className="h-3 w-3" />Style Presets</p>
        {presets?.length > 0 && (
          <Select onValueChange={(id) => onApplyPreset(id)}>
            <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="text-preset-apply-select"><SelectValue placeholder="Apply a saved preset…" /></SelectTrigger>
            <SelectContent>{presets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <div className="flex gap-2">
          <Input value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="New preset name" className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="text-preset-name-input" />
          <Button size="sm" variant="outline" className="h-8 border-white/10 shrink-0" disabled={!presetName.trim()} onClick={() => { onSaveAsPreset(presetName.trim()); setPresetName(''); }} data-testid="text-preset-save-button">Save</Button>
        </div>
      </div>
    </div>
  );
}
